import { FC, useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import classnames from 'classnames'
import { capitalize } from 'lodash'

import LabelBadge from 'components/LabelBadge'
import TableSortLink from 'components/TableSortLink'
import NoResultsFound from 'components/NoResultsFound'

import AdjustStockOnHandModal from '../AdjustStockOnHandModal'
import { useMutation } from 'hooks/useMutation'
import { Draggable, DraggableRow } from './Draggable'
import ProductImage from './ProductImage'

import GET_PRODUCTS_LIST from './graphql/GetProductsList.graphql'
import GET_PUBLISH_STATE from '../PublishingContext/graphql/GetPublishState.graphql'
import UPDATE_PRODUCT_POSITION_MUTATION from './graphql/UpdateProductPositionMutation.graphql'
import { GetProductsList_currentSeller_catalog_draftProducts } from './graphql/__generated__/GetProductsList'
import { usePublishingContext } from 'modules/seller-hub/products/components/PublishingContext/usePublishingContext'
import { ProductSortingEnum as S } from '../../../../../../__generated__/globalTypes'
import {
  UpdateProductPositionMutation,
  UpdateProductPositionMutationVariables
} from './graphql/__generated__/UpdateProductPositionMutation'

import styles from './ProductsTable.module.css'

export type ProductsTableProps = {
  data: GetProductsList_currentSeller_catalog_draftProducts
  catalogId: string
  updatedProductId?: string
  refreshFirstImage?: boolean
  returnTo?: string
  outOfStockThreshold: number | null | undefined
  currencySymbol: string
}

const ProductTable: FC<ProductsTableProps> = ({
  data,
  catalogId,
  updatedProductId,
  refreshFirstImage,
  returnTo,
  outOfStockThreshold,
  currencySymbol
}) => {
  const [rowData, setRowData] = useState(data.nodes)
  const [positionChangeInProgress, setPositionChangeInProgress] = useState<boolean>(false)
  const [draggedRow, setDraggedRow] = useState<string>()
  const { isQueued } = usePublishingContext()

  const baseUrl = `${document.location.origin}${document.location.pathname}`.replace('/products', '')

  const [updateProductPosition] = useMutation<UpdateProductPositionMutation, UpdateProductPositionMutationVariables>(
    UPDATE_PRODUCT_POSITION_MUTATION,
    {
      refetchQueries: [GET_PRODUCTS_LIST, GET_PUBLISH_STATE]
    }
  )

  const handleDragRowChange = (dragIndex: number, hoverIndex: number) => {
    setRowData(prevState => {
      const clonedRows = prevState.slice()
      const draggedRow = prevState.slice(dragIndex, dragIndex + 1)[0]
      clonedRows.splice(dragIndex, 1)
      clonedRows.splice(hoverIndex, 0, draggedRow)
      return clonedRows
    })
  }

  const handleRowChange = useCallback(
    async (id: string, name: string, newPosition: number) => {
      setPositionChangeInProgress(true)
      await updateProductPosition({
        variables: {
          catalogId,
          newPosition,
          productId: id,
          productName: name
        },
        update: (cache, result) => {
          const sellerId = result?.data?.updateProduct?.product?.catalog.sellerId
          cache.evict({ id: `Seller:${sellerId}`, fieldName: 'draftProducts' })
          cache.gc()
        }
      })
    },
    [catalogId, updateProductPosition]
  )

  useEffect(() => {
    if (data?.nodes) {
      setRowData(data.nodes)
      setPositionChangeInProgress(false)
    }
  }, [data.nodes])

  return (
    <div>
      <table className={classnames(styles.table, { [styles.positionChangeInProgress]: positionChangeInProgress })}>
        <thead className={styles.header}>
          <tr className={styles.headerRow}>
            <TableSortLink<S> title="Position" sortBy={S.POSITION} align="right" />
            <th></th>
            <TableSortLink<S> title="Name" sortBy={S.PRODUCT_NAME} />
            <TableSortLink<S> title="Custom Tags" sortBy={S.CUSTOM_TAGS} />
            <TableSortLink<S> title="Label" sortBy={S.LABELS} align="center" />
            <TableSortLink<S> title="Minimum" sortBy={S.MINIMUM} align="center" />
            <TableSortLink<S> title="Stock on Hand" sortBy={S.SOH} align="center" />
            <TableSortLink<S> title="Price" sortBy={S.PRICE} align="right" className={styles.priceContainer} />
            <th></th>
          </tr>
        </thead>
        <tbody>
          <Draggable>
            {rowData?.length > 0 &&
              rowData.map((product, i) => {
                const variety = product.firstVariety
                const varietyCount = product.varieties.nodes.length ?? 0
                const sku = product.firstVariety?.sku
                const isStack = product.varieties.nodes.length > 1
                const picture = product.firstPicture
                const shouldRefreshProductImage = product.id === updatedProductId && refreshFirstImage
                const outOfStock = (variety?.stockOnHand ?? 0) <= (outOfStockThreshold ?? 0)

                const productId = product.id

                const position = <td className={styles.right}>{product.position}</td>
                const image = (
                  <td>
                    <div className={styles.img}>
                      <ProductImage
                        photoUrl={picture?.gridPhotoUrl}
                        iconSize={18}
                        shouldRefresh={shouldRefreshProductImage}
                        catalogId={catalogId}
                        productId={product.id}
                      />
                    </div>
                  </td>
                )
                const productName = (
                  <td>
                    <div className={styles.productNameContainer}>
                      <p className={styles.name}>
                        <Link href={`${baseUrl}/products/${product.id}/edit${returnTo}`}>{product.name}</Link>
                      </p>
                      {!isStack && <p className={styles.sku}>{sku}</p>}
                    </div>
                  </td>
                )
                const customTags = (
                  <td className={styles.customTagsCell}>
                    {product.customTags
                      // This is a bit convoluted, but basically:
                      // 1. The map function checks if the custom tag name starts with %. These are special tags, that should appear in SellerHub only, not Marketplace / iPad app
                      // 2. If it does, it wraps the tag name in a span element with the styles.organisationalTag class.
                      // 3. Otherwise, it returns the tag name as a string.
                      // 4. The sort function sorts the elements by their names, converting span elements back to strings for comparison.
                      // 5. The reduce function adds commas between the elements.
                      .map(c => {
                        if (c.name?.startsWith('%')) {
                          return (
                            <span className={styles.organisationalTag} key={c.name}>
                              {c.name}
                            </span>
                          )
                        } else {
                          return c.name
                        }
                      })
                      .filter((c): c is string | JSX.Element => c !== null) // Filter out null values and ensure correct type
                      .sort((a, b) => {
                        // Convert elements to strings for sorting
                        const nameA = typeof a === 'string' ? a : a.props.children
                        const nameB = typeof b === 'string' ? b : b.props.children
                        return nameA.localeCompare(nameB)
                      })
                      .reduce<(string | JSX.Element)[]>((acc, curr, index) => {
                        if (index === 0) {
                          return [curr]
                        }
                        return [...acc, ', ', curr]
                      }, [])}
                  </td>
                )
                const label = <td className={styles.center}>{product.badge && <LabelBadge id={product.badge} />}</td>
                const minimum = <td className={styles.center}>{variety?.minimumOrderQuantity}</td>
                const stock = (
                  <td className={classnames(styles.center)}>
                    <div className={styles.stockOnHandContainer}>
                      <p
                        className={classnames(styles.stockOnHand, {
                          [styles.stockBelowThreshold]: outOfStock
                        })}>
                        {variety?.stockOnHand}
                      </p>
                      <AdjustStockOnHandModal varietyId={variety?.id ?? ''} />
                    </div>
                  </td>
                )
                const price = (
                  <td className={classnames(styles.priceContainer, styles.right)}>
                    {!isStack ? (
                      <>
                        <p className={styles.itemPrice}>{`${currencySymbol}${product.prices[0].toFixed(2)}`}</p>
                        <p className={styles.itemUnit}>{capitalize(variety?.unit ?? '')}</p>
                      </>
                    ) : (
                      <>
                        <p className={styles.itemPrice}>
                          {`${currencySymbol}${product.prices[0].toFixed(2)}`} -{' '}
                          {`${currencySymbol}${product.prices?.[product.prices.length - 1].toFixed(2)}`}
                        </p>
                      </>
                    )}
                  </td>
                )

                return (
                  <DraggableRow
                    index={i}
                    key={productId}
                    productInfo={{
                      id: product.id,
                      initialIndex: i,
                      name: product.name,
                      position: product.position
                    }}
                    setDraggedRow={setDraggedRow}
                    handleRowChange={handleRowChange}
                    handleDragRowChange={handleDragRowChange}
                    loading={positionChangeInProgress && draggedRow === product.id}
                    className={classnames(styles.row, {
                      [styles.rowAlt]: i % 2 === 0,
                      [styles.productIsHidden]: product.hidden
                    })}
                    disable={isQueued}
                    varietyCount={varietyCount}
                    outOfStockThreshold={outOfStockThreshold}
                    rowChildren={price}>
                    {!isStack ? (
                      <>
                        {position}
                        {image}
                        {productName}
                        {customTags}
                        {label}
                        {minimum}
                        {stock}
                        {price}
                      </>
                    ) : (
                      <>
                        {position}
                        {image}
                        {productName}
                        {customTags}
                        {label}
                      </>
                    )}
                  </DraggableRow>
                )
              })}
          </Draggable>
        </tbody>
      </table>
      {rowData.length === 0 && <NoResultsFound />}
    </div>
  )
}

export default ProductTable
